/**
 * Table of Contents
 * Right-panel overlay for slide navigation with appendix accordion and PDF download
 */

import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { logError, trackCtaClick } from '@logging';
import { downloadDeckFile } from '@components/press/pitch-deck/export-pdf';
import type { DeckEdition, SlideDefinition } from '@components/press/pitch-deck/pitch-deck.types';
import {
  TOCBackdrop,
  TOCPanel,
  TOCHeader,
  TOCTitle,
  TOCCloseButton,
  TOCList,
  TOCItem,
  TOCAccordionHeader,
  TOCAccordionLabel,
  TOCChevron,
  TOCDownloadSection,
  TOCDownloadButton,
} from '@components/press/pitch-deck/table-of-contents.styles';

interface TableOfContentsProps {
  title: string;
  slides: SlideDefinition[];
  /** The edition's PDF/PPTX; without them the panel offers no downloads. */
  downloads?: DeckEdition['downloads'];
  currentSlide: number;
  onNavigate: (index: number) => void;
  onClose: () => void;
  isOpen: boolean;
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({
  title,
  slides,
  downloads,
  currentSlide,
  onNavigate,
  onClose,
  isOpen,
}) => {
  const [appendixOpen, setAppendixOpen] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Auto-open accordion when viewing an appendix slide
  useEffect(() => {
    if (slides[currentSlide]?.category === 'appendix') {
      setAppendixOpen(true);
    }
  }, [currentSlide, slides]);

  const mainSlides = slides.filter(s => s.category === 'main');
  const appendixOverview = slides.find(s => s.id === 'appendix-overview');
  const appendixSubSlides = slides.filter(
    s => s.category === 'appendix' && s.id !== 'appendix-overview'
  );
  const appendixOverviewIndex = appendixOverview ? slides.indexOf(appendixOverview) : -1;
  const isOnAppendixOverview = appendixOverviewIndex === currentSlide;

  const handleItemClick = (index: number) => {
    onNavigate(index);
    onClose();
  };

  const handleAppendixHeaderClick = () => {
    if (appendixOverviewIndex >= 0) {
      onNavigate(appendixOverviewIndex);
      onClose();
    }
  };

  const handleToggleAccordion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAppendixOpen(prev => !prev);
  };

  /* The CTA is emitted before the download starts, not after. The file is
     pulled from a GCS bucket by a synthetic `<a download>` click, so nothing
     here observes whether it completed.

     These used to `await import('./export-pdf')`. That was removed rather
     than guarded, because the split bought nothing and cost a real failure:

       - export-pdf.ts has no imports at all. It is two URL constants and a
         seven-line function that builds an `<a download>`. Splitting it saves
         no bundle weight — the usual reason to lazy-load an exporter is a
         heavy dependency like jsPDF, and there isn't one here. The PDF and
         PPTX are generated offline by scripts/generate-deck-pdf.cjs.
       - It added a network round-trip between the click and the download.
       - It added a failure mode. These are async onClick handlers, so a
         chunk that fails to load rejects with nobody catching it. Production
         logged exactly that: "error loading dynamically imported module:
         https://homeceo.ai/assets/<hash>.js" arriving as a global unhandled
         rejection, anonymous and unattributable.

     The user-facing version of that is the part worth keeping in mind: an
     investor clicks Download PDF on the deck and *nothing happens at all* —
     no file, no error, no feedback. The most likely trigger is the most
     ordinary one: a tab left open across a deploy, after which the hashed
     asset it wants no longer exists.

     A static import cannot fail this way; the code is in the bundle that is
     already running. The try/catch below is for the residual case where the
     DOM calls themselves throw, and exists so that failure is attributable
     to this call site rather than arriving as another anonymous rejection. */
  const handleDownloadPdf = () => {
    if (!downloads) return;
    trackCtaClick('deck_download_pdf', 'pitch_deck_toc');
    try {
      downloadDeckFile(downloads.pdf);
    } catch (cause) {
      logError('deck PDF download failed', {
        location: 'pitch_deck_toc',
        cause: cause instanceof Error ? cause : undefined,
      });
    }
  };

  const handleDownloadPptx = () => {
    if (!downloads) return;
    trackCtaClick('deck_download_pptx', 'pitch_deck_toc');
    try {
      downloadDeckFile(downloads.pptx);
    } catch (cause) {
      logError('deck PPTX download failed', {
        location: 'pitch_deck_toc',
        cause: cause instanceof Error ? cause : undefined,
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <TOCBackdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <TOCPanel
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <TOCHeader>
              <TOCTitle>{title}</TOCTitle>
              <TOCCloseButton onClick={onClose}>&times;</TOCCloseButton>
            </TOCHeader>

            <TOCList>
              {mainSlides.map((slide) => {
                const globalIndex = slides.indexOf(slide);
                return (
                  <TOCItem
                    key={slide.id}
                    $active={globalIndex === currentSlide}
                    onClick={() => handleItemClick(globalIndex)}
                  >
                    {slide.title}
                  </TOCItem>
                );
              })}
            </TOCList>

            {(appendixOverview || appendixSubSlides.length > 0) && (
              <>
                <TOCAccordionHeader
                  $active={isOnAppendixOverview}
                  onClick={handleAppendixHeaderClick}
                >
                  <TOCAccordionLabel $active={isOnAppendixOverview}>
                    Appendix
                  </TOCAccordionLabel>
                  <TOCChevron
                    onClick={handleToggleAccordion}
                    animate={{ rotate: appendixOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </TOCChevron>
                </TOCAccordionHeader>

                <AnimatePresence initial={false}>
                  {appendixOpen && appendixSubSlides.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <TOCList>
                        {appendixSubSlides.map((slide) => {
                          const globalIndex = slides.indexOf(slide);
                          return (
                            <TOCItem
                              key={slide.id}
                              $active={globalIndex === currentSlide}
                              onClick={() => handleItemClick(globalIndex)}
                            >
                              {slide.title}
                            </TOCItem>
                          );
                        })}
                      </TOCList>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {downloads && (
              <TOCDownloadSection>
                <TOCDownloadButton onClick={handleDownloadPdf}>
                  Download PDF
                </TOCDownloadButton>
                <TOCDownloadButton onClick={handleDownloadPptx}>
                  Download PPTX
                </TOCDownloadButton>
              </TOCDownloadSection>
            )}
          </TOCPanel>
        </>
      )}
    </AnimatePresence>
  );
};
